import React, { FC, useState } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import moment, { Moment } from 'moment-timezone';

import { isTouchSupported, isViewportTooSmall } from '@utils/helpers';
import { CalendarButton } from '../Calendar';

import {
  InputWrapper,
  MonthName,
  StyledPicker,
  StyledMobileInputWrapper,
} from './StartDatePicker.styles';
import { SingleDatePicker } from 'react-dates';
import { useId } from 'react-id-generator';
import { ChevronRightIcon, ChevronLeftIcon } from '@root/styles/icons';
import { DATE_FORMAT } from '../DateRangeInput/constants';

type StartDatePickerProps = {
  input: {
    value: Moment;
    onChange: (date: Moment) => void;
  };
  handleAfterChange?: (date: Date, date2: Moment) => void;
  minDate?: Moment;
  maxDate?: Moment;
  pickerTextId: number | string;
  someVariable?: string;
  popperModifiers?: any;
};

export const StartDatePicker: FC<StartDatePickerProps> = ({
  input: { value, onChange },
  handleAfterChange,
  minDate = moment(),
  maxDate = null,
  pickerTextId,
  ...props
}) => {
  const [startDateMobileInputId] = useId();
  const [focused, setFocused] = useState(false);

  const intl = useIntl();
  const timeZone = intl.timeZone || '';
  const handleChange = (date) => {
    onChange(date);
    if (handleAfterChange) {
      handleAfterChange(date, moment(date).add(7, 'days'));
    }
  };

  const handleNativeChange = (date) => {
    const dateValue = moment.tz(date, timeZone);
    handleChange(dateValue);
  };

  const handlePickerChange = (value) => {
    const parsedValue = moment.tz(moment(value).format('YYYY-MM-DD'), timeZone);
    handleChange(parsedValue);
  };

  const displayNativePicker = isTouchSupported() && isViewportTooSmall(550, 720);
  const parsedValue = value ? moment(value).format('YYYY-MM-DD') : undefined;

  const minDateNative = minDate?.format('YYYY-MM-DD');
  const maxDateNative = maxDate?.format('YYYY-MM-DD');
  return (
    <InputWrapper>
      <MonthName>
        <FormattedDate value={parsedValue} year="numeric" month="long" />
      </MonthName>
      <div>
        {displayNativePicker && (
          <StyledMobileInputWrapper>
            <CalendarButton type="button" onClick={() => setFocused(true)} />
            <SingleDatePicker
              focused={focused}
              onFocusChange={({ focused }) => setFocused(focused)}
              hideKeyboardShortcutsPanel
              date={value}
              id={startDateMobileInputId}
              onDateChange={handleNativeChange}
              displayFormat={DATE_FORMAT}
              navNext={<ChevronRightIcon />}
              navPrev={<ChevronLeftIcon />}
              numberOfMonths={1}
            />
          </StyledMobileInputWrapper>
        )}
        {!displayNativePicker && (
          // @ts-ignore Mismatches with the typings of react-datepicker a lot
          // @todo 2024-03-24 Review if this works
          <StyledPicker
            {...props}
            selected={parsedValue && new Date(parsedValue)}
            onChange={handlePickerChange}
            minDate={new Date(minDateNative)}
            maxDate={maxDateNative && new Date(maxDateNative)}
            customInput={<CalendarButton textId={pickerTextId} />}
          />
        )}
      </div>
    </InputWrapper>
  );
};
